import { useState } from 'react'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import StartStep from './Steps/StartStep'
import ErrorStep from './Steps/ErrorStep'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { captureException } from '@sentry/nextjs'

/**
 * SinglePageLayout component for checkout flow
 * Handles checkout errors and displays appropriate step components
 * @returns {JSX.Element} Current checkout step component
 */
const SinglePageLayout = () => {
  const { data: checkoutData } = useHeadlessCheckoutContext()
  const [component, setComponent] = useState(<StartStep />)

  const backToStartStep = () => {
    setComponent(<StartStep />)
  }

  /**
   * Determines if a checkout error is already handled by the UI
   * @param {Object} e - Error object from checkout
   * @param {Array<string>} [e.field] - Array of field names associated with the error
   * @param {string} e.code - Error code
   * @returns {boolean} True if the error is already handled
   */
  function errorIsHandled(e) {
    const handledFields = ['discountCode', 'giftCardCodes', 'quantity']
    const ignoredCodes = ['INVALID_DELIVERY_OPTION']
    return (
      (Array.isArray(e.field) && e.field.some((f) => handledFields?.includes(f))) ||
      ignoredCodes?.includes(e.code)
    )
  }

  const unhandledCheckoutErrors =
    checkoutData?.userErrors?.filter((e) => !errorIsHandled(e)) || []

  useDeepCompareEffect(() => {
    if (unhandledCheckoutErrors && unhandledCheckoutErrors.length > 0) {
      unhandledCheckoutErrors.forEach((error) =>
        captureException(new Error(JSON.stringify(error))),
      )
      setComponent(
        <ErrorStep
          backToStartStep={backToStartStep}
          errors={unhandledCheckoutErrors}
        />,
      )
    }
  }, [checkoutData])

  return component
}

export default SinglePageLayout
